/**
 * Acquisition / attribution capture.
 *
 * Runs once per session: on the customer's first landing we snapshot
 *   • UTM params (utm_source/medium/campaign/term/content)
 *   • Paid click IDs (gclid, fbclid, oppref — OpenAI/ChatGPT Ads)
 *   • document.referrer
 *   • the landing path itself
 *
 * Persisted in sessionStorage under `pg_attr_v1`. The capture is sticky for
 * the duration of the browser session — even if the customer browses to
 * other internal pages before reaching checkout, the FIRST-touch attribution
 * is what we attach to the order. This matches Meta/Google's last-paid-click
 * attribution convention more closely than the noisy "last referrer"
 * snapshot most carts use.
 *
 * The captured payload is read by Checkout.jsx and attached to the order
 * create payload. The backend stores the raw fields and the admin
 * /api/admin/attribution endpoint buckets them into a single source.
 */

const STORAGE_KEY = "pg_attr_v1";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const CLICK_ID_KEYS = ["gclid", "fbclid", "oppref"];

function readSnapshot() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeSnapshot(snap) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch (_) { /* sessionStorage disabled (private mode) */ }
}

/**
 * Capture acquisition signals from the current URL + referrer if we
 * haven't yet captured them this session. Safe to call multiple times —
 * first call wins.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  const existing = readSnapshot();
  if (existing) return existing;

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const snap = {
    captured_at: new Date().toISOString(),
    landing_path: url.pathname + url.search,
    referrer: document.referrer || null,
  };
  let hasSignal = false;

  for (const k of UTM_KEYS) {
    const v = (params.get(k) || "").trim();
    if (v) { snap[k] = v; hasSignal = true; }
  }
  for (const k of CLICK_ID_KEYS) {
    const v = (params.get(k) || "").trim();
    if (v) { snap[k] = v; hasSignal = true; }
  }

  // Always persist on first session touch — even with no UTM/click IDs.
  // A "direct" or "referral" first touch is itself useful attribution
  // (vs leaving the slot empty and falling back to last-touch heuristics).
  // The `hasSignal` flag is just for an internal debug breadcrumb.
  snap._has_signal = hasSignal;
  writeSnapshot(snap);
  return snap;
}

/**
 * Return the captured attribution snapshot for attaching to an order
 * create payload. Returns an empty object if nothing was captured.
 */
export function getAttributionForOrder() {
  const snap = readSnapshot();
  if (!snap) return {};
  const out = {};
  for (const k of [...UTM_KEYS, ...CLICK_ID_KEYS, "referrer", "landing_path"]) {
    if (snap[k]) out[k] = String(snap[k]).slice(0, 500);
  }
  return out;
}

/** Clear the snapshot — call after order confirmation if we want to
 * reset for the customer's next session. NOT called from Checkout because
 * sessionStorage already clears on tab close. */
export function clearAttribution() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
}
